import { Grid } from '../../core/Grid.js';
import {
    Position,
    positionKey,
    parsePositionKey,
    isInside,
} from '../../core/GridGeometry.js';
import { ScoreBreakdown, calculateScore } from '../../core/Scoring.js';
import { VortexGateManager } from '../VortexGateManager.js';
import { IVortexGame } from '../IVortexGame.js';
import { TurnsGame, TurnType, CartStep, CartPath } from './TurnsGame.js';

/**
 * Vortex variant of Turns: an active gate teleports the cart to its paired gate, keeping
 * its heading, and it emerges on the far side — the same heading-preserving portal Mirrors
 * uses. Everything else (the cart drive, the exactly-twice win) is inherited unchanged.
 */
export class TurnsVortex extends TurnsGame implements IVortexGame {
    private readonly gm = new VortexGateManager();

    constructor(grid: Grid) {
        super(grid);
    }

    // ── Vortex Data ───────────────────────────────────────────────

    setVortexData(gatePairs: Array<[Position, Position]>, allGates: Set<string>): void {
        this.gm.setVortexData(gatePairs, allGates, () => { this.cachedPath = null; });
    }

    // ── Gate Queries ──────────────────────────────────────────────

    isGateCell(pos: Position): boolean {
        return this.gm.isGateCell(pos);
    }

    isActiveGate(pos: Position): boolean {
        return this.gm.isActiveGate(pos);
    }

    canActivateGate(pos: Position): boolean {
        return this.gm.canActivateGate(pos, (p, paired) => {
            if (this.turns.has(positionKey(p)) || this.turns.has(positionKey(paired))) return false;
            if (this.grid.hasDigitAt(p) || this.grid.hasDigitAt(paired)) return false;
            return true;
        });
    }

    toggleVortexGate(pos: Position): boolean {
        return this.gm.toggleVortexGate(pos, this.canActivateGate(pos), () => { this.cachedPath = null; });
    }

    getActiveGatePairs(): Map<string, string> {
        return this.gm.getActiveGatePairs();
    }

    getVortexGatePairs(): Array<[Position, Position]> {
        return this.gm.getVortexGatePairs();
    }

    getAllGatePositions(): Set<string> {
        return this.gm.getAllGatePositions();
    }

    // ── Placement Blocking ────────────────────────────────────────

    override setTurn(pos: Position, turn: TurnType | null): void {
        if (turn !== null && this.gm.isActiveGate(pos)) return;
        super.setTurn(pos, turn);
    }

    override cycleTurn(pos: Position): void {
        if (this.gm.isActiveGate(pos)) return;
        super.cycleTurn(pos);
    }

    // ── State Management ──────────────────────────────────────────

    override reset(): void {
        super.reset();
        this.gm.clearActiveGates();
    }

    override isStarted(): boolean {
        return super.isStarted() || this.gm.hasActiveGates();
    }

    // ── Drive with teleportation ──────────────────────────────────

    override traceCart(): CartPath {
        if (this.cachedPath) return this.cachedPath;
        if (!this.gm.hasActiveGates()) return super.traceCart();

        const steps: CartStep[] = [];
        const seen = new Set<string>();

        let pos: Position = { ...TurnsGame.START };
        let teleported = false;
        let { dir, uturn } = this.resolveOut(pos, TurnsGame.START_DIR);
        const startKey = `${positionKey(pos)}:${dir}:${teleported}`;

        let loopClosed = false;
        const maxIterations = this.grid.size * this.grid.size * 8 + 4;
        let iterations = 0;

        while (iterations++ < maxIterations) {
            const key = `${positionKey(pos)}:${dir}:${teleported}`;
            if (seen.has(key)) {
                loopClosed = key === startKey;
                break;
            }
            seen.add(key);
            steps.push({ pos: { ...pos }, dir, uturn, teleport: teleported });

            const next = this.grid.move(pos, dir);
            if (!isInside(next)) break;

            // Teleport when landing on an active gate (but not immediately after a jump,
            // so the paired gate lets the cart pass through and emerge on the far side).
            const pairedKey = this.gm.raw.get(positionKey(next));
            if (pairedKey && !teleported) {
                pos = parsePositionKey(pairedKey);
                teleported = true;
            } else {
                pos = next;
                teleported = false;
            }
            ({ dir, uturn } = this.resolveOut(pos, dir));
        }

        this.cachedPath = this.summarize(steps, loopClosed);
        return this.cachedPath;
    }

    // ── Scoring ────────────────────────────────────────────────────

    override getScore(): ScoreBreakdown | null {
        if (!this.checkWin()) return null;

        const drivenCells = new Set<string>();
        for (const step of this.traceCart().steps) {
            if (!this.grid.hasDigitAt(step.pos)) drivenCells.add(positionKey(step.pos));
        }
        return calculateScore(this.getTurns().size, this.gm.deduplicateCellCount(drivenCells));
    }

    // ── Serialization ─────────────────────────────────────────────

    override serialize(): string {
        return this.gm.serializeWithBase(super.serialize(), this.gs);
    }

    override deserialize(serialized: string): void {
        this.gm.clearActiveGates();
        if (!serialized) {
            super.deserialize('');
            return;
        }
        const { basePart, gatePart } = VortexGateManager.splitSerialized(serialized);
        super.deserialize(basePart);
        this.gm.deserializeGates(gatePart, this.gs);
        this.cachedPath = null;
    }
}
